import React, { useState } from 'react';

const FAQS = [
  {
    q: 'How do I get my account verified?',
    a: 'Simply upload a clear photo of your passport, driving licence, or National ID during registration. The administrative team reviews registrations 24/7. Most accounts are approved within a few hours.',
  },
  {
    q: 'Is my location shared all the time?',
    a: 'Absolutely not. RAKSHA respects your privacy. Your GPS location coordinates are only captured and broadcast to verified responders during an active emergency SOS alert. In idle times, tracking is inactive.',
  },
  {
    q: 'What happens if no one responds to my SOS?',
    a: 'The alert remains active until someone responds or you cancel it manually. We strongly recommend simultaneously calling 999/112 in any serious emergency. RAKSHA complements — it does not replace — emergency services.',
  },
  {
    q: 'Can I be a responder and a user at the same time?',
    a: 'Yes. Every verified RAKSHA member can both trigger SOS alerts and respond to alerts from others. You are automatically part of the local responder network once approved.',
  },
];

export default function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = e => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = e => {
    e.preventDefault();
    // In production this would POST to a backend or email service
    setSubmitted(true);
  };

  return (
    <main>
      {/* Page Hero */}
      <div className="page-hero">
        <div className="rk-container">
          <span className="page-hero-label">Support Desk</span>
          <h1 className="page-hero-title">Contact RAKSHA Ireland</h1>
          <p className="page-hero-sub">
            Have a question, feedback, or need help with account approval? Our team will assist you.
          </p>
        </div>
      </div>

      {/* Contact Grid */}
      <section className="rk-section">
        <div className="rk-container">
          <div className="contact-grid">
            {/* Left: Info */}
            <div>
              <h2 style={{ fontSize: '1.4rem', fontWeight: 700, marginBottom: 20 }}>We're here to help</h2>
              <p style={{ color: 'var(--rk-muted)', fontSize: '0.93rem', lineHeight: 1.7, marginBottom: 28 }}>
                Our Dublin-based volunteer administrative team reviews and approves identity profiles 24/7 to ensure
                RAKSHA Ireland remains a completely verified, safe network.
              </p>

              <div className="contact-info-card">
                <div className="contact-info-label">Email Support</div>
                <div className="contact-info-value">
                  <a href="mailto:support@rakshaireland.ie">support@rakshaireland.ie</a>
                </div>
              </div>

              <div className="contact-info-card">
                <div className="contact-info-label">Location</div>
                <div className="contact-info-value">Dublin, Ireland<br /><span style={{ fontSize: '0.85rem', color: 'var(--rk-muted)' }}>Serving all of Ireland</span></div>
              </div>

              <div className="contact-info-card">
                <div className="contact-info-label">Admin Working Hours</div>
                <div className="contact-info-value">Monday – Sunday<br /><span style={{ fontSize: '0.85rem', color: 'var(--rk-muted)' }}>24/7 Active ID Approval Network</span></div>
              </div>

              <div style={{ marginTop: 8 }}>
                <p style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--rk-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
                  Connect with us
                </p>
                <div className="contact-socials">
                  <a href="https://twitter.com/" target="_blank" rel="noreferrer" className="contact-social">
                    <i className="bi bi-twitter-x"></i>
                  </a>
                  <a href="https://instagram.com/" target="_blank" rel="noreferrer" className="contact-social">
                    <i className="bi bi-instagram"></i>
                  </a>
                  <a href="mailto:support@rakshaireland.ie" className="contact-social">
                    <i className="bi bi-envelope-fill"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div>
              <div className="contact-form">
                {submitted ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <div style={{ fontSize: '3rem', marginBottom: 16 }}>✅</div>
                    <h3 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 10 }}>Message Sent!</h3>
                    <p style={{ color: 'var(--rk-muted)', fontSize: '0.92rem' }}>
                      Thanks for reaching out. Our team will get back to you within 2–4 hours.
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', subject: '', message: '' }); }}
                      style={{ marginTop: 20, background: 'var(--rk-bg)', border: '1px solid var(--rk-border)', padding: '10px 24px', borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}
                    >
                      Send Another
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="contact-form-title">Send a message</h3>
                    <p className="contact-form-sub">Fill in your details and we will get back to you within 2–4 hours.</p>
                    <form onSubmit={handleSubmit}>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label" htmlFor="name">Full Name</label>
                          <input id="name" name="name" className="form-input" placeholder="Your name" value={formData.name} onChange={handleChange} required />
                        </div>
                        <div className="form-group">
                          <label className="form-label" htmlFor="email">Email Address</label>
                          <input id="email" name="email" type="email" className="form-input" placeholder="your@email.com" value={formData.email} onChange={handleChange} required />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="subject">Subject</label>
                        <select id="subject" name="subject" className="form-select" value={formData.subject} onChange={handleChange} required>
                          <option value="">Select a subject…</option>
                          <option value="account-approval">Account Approval</option>
                          <option value="technical-issue">Technical Issue</option>
                          <option value="privacy-data">Privacy / Data Request</option>
                          <option value="feedback">Feedback</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="message">Message</label>
                        <textarea id="message" name="message" className="form-textarea" placeholder="Tell us how we can help…" value={formData.message} onChange={handleChange} required />
                      </div>
                      <button type="submit" className="form-submit">
                        <i className="bi bi-send-fill"></i>
                        Send Message
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick FAQs */}
      <section className="rk-section rk-section-light">
        <div className="rk-container" style={{ maxWidth: 760 }}>
          <div className="rk-section-header">
            <span className="rk-section-label">Quick Solutions</span>
            <h2 className="rk-section-title">Common Queries</h2>
            <p className="rk-section-sub">Check these quick solutions before sending a message.</p>
          </div>
          <div className="contact-faq-list">
            {FAQS.map((f, i) => (
              <div className="contact-faq-item" key={i}>
                <button className="contact-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <strong>{f.q}</strong>
                  <i className={`bi bi-chevron-${openFaq === i ? 'up' : 'down'}`} style={{ float: 'right', color: 'var(--rk-muted)' }}></i>
                </button>
                {openFaq === i && <p className="contact-faq-a">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
